import { BlockedPhoneNumber } from '@provision/entity-types';
import { useMutation } from '@provision/graphql-client';
import { useCallback } from 'react';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { BLOCKED_PHONE_NUMBER_FRAGMENT } from 'src/graphql/entities/blocked-phone-number/blocked-phone-number.fragments';
import { CREATE_BLOCKED_PHONE_NUMBER_QUERY } from 'src/graphql/entities/blocked-phone-number/blocked-phone-number.operations';
import { useCurrentUserProfile } from 'src/graphql/entities/platform-user';
import { useGlobalStore } from 'src/store';
import { useNotifications } from 'src/utils/hooks';
import { GetBlockedPhoneNumberQuery } from './use-blocked-phone-number';

export interface CreateBlockedPhoneNumberMutation {
  createBlockedPhoneNumber: Omit<
    BlockedPhoneNumber,
    'createdByPlatformUser' | 'updatedAt' | 'partner'
  >[];
}

export type CreateBlockedPhoneNumberMutationParams = {
  data: Pick<
    BlockedPhoneNumber,
    'blockedPhoneNumber' | 'partnerId' | 'createdByPlatformUserId'
  >;
};

export const useCreateBlockedPhoneNumber = () => {
  const partnerId = useGlobalStore.use.selectedPartnerId() || PLATFORM_PARTNER_ID;
  const { user } = useCurrentUserProfile();
  const { showErrorAlert } = useNotifications();

  const [createBlockedPhoneNumberMutation, { loading: isCreating }] = useMutation<
    CreateBlockedPhoneNumberMutation,
    CreateBlockedPhoneNumberMutationParams
  >(CREATE_BLOCKED_PHONE_NUMBER_QUERY, {
    update(cache, { data }) {
      if (!data?.createBlockedPhoneNumber?.length) {
        return;
      }

      cache.modify({
        fields: {
          getBlockedPhoneNumber(cachedRef = {}) {
            const cachedData =
              cachedRef as GetBlockedPhoneNumberQuery['getBlockedPhoneNumber'];
            const newBlockedPhoneNumberRef = cache.writeFragment({
              data: data.createBlockedPhoneNumber[0],
              fragment: BLOCKED_PHONE_NUMBER_FRAGMENT,
            });

            return {
              ...cachedData,
              records: [
                ...(cachedData.records?.length ? cachedData.records : []),
                newBlockedPhoneNumberRef,
              ],
              metadata: {
                ...cachedData.metadata,
                totalRecords: (cachedData.metadata?.totalRecords ?? 0) + 1,
              },
            };
          },
        },
      });
    },
    optimisticResponse: (params) => ({
      createBlockedPhoneNumber: [
        {
          __typename: 'BlockedPhoneNumber',
          id: new Date().getTime().toString(),
          blockedPhoneNumberId: new Date().getTime().toString(),
          blockedPhoneNumber: params.data.blockedPhoneNumber,
          partnerId: params.data.partnerId,
          createdAt: new Date().toLocaleTimeString(),
          createdByPlatformUserId: user?.userId || '',
        },
      ],
    }),
    onError: (error) => {
      console.error(error);
      showErrorAlert('Failed to create blocked phone number, please try again');
    },
  });

  const createBlockedPhoneNumber = useCallback(
    async (blockedPhoneNumber: string) => {
      if (!user?.userId) {
        console.error('Cannot create blocked phone number. Current user is not found');
        return;
      }
      await createBlockedPhoneNumberMutation({
        variables: {
          data: {
            blockedPhoneNumber,
            partnerId,
            createdByPlatformUserId: user.userId,
          },
        },
      });
    },
    [createBlockedPhoneNumberMutation, partnerId, user?.userId],
  );

  return {
    createBlockedPhoneNumber,
    isCreating,
  };
};
